import type {
  FloorPlannerCamera,
  FloorPlannerDesign,
  FloorPlannerLayout,
  FloorPlannerPoint,
} from '@repo/frontend-types';
import { asArray, findLatestDesign, isLive } from '@repo/front-end-utils';

import { getCameraSight } from './cameraSight';

/**
 * Which feed is likely to see which entity — in both directions, from one rule.
 *
 * ## Entity → cameras: ordering, never filtering, and the measurement behind that
 *
 * The obvious design is "show the cameras that can see this". It is wrong here, and both ways it
 * can be wrong were measured on production on 2026-09-10. Of the four cameras ever placed on a
 * wizard plan:
 *
 * - **Three** carry the camera catalogue's only usable entry — an outdoor bullet camera with a
 *   **177 m** range and a 53° spread. In a gym a 177 m cone covers the entire building, so a filter
 *   on "can see it" admits everything and the shortlist is not a shortlist.
 * - **One** (Finsbury Park's) has no type at all, so `detectionDistance` was null and
 *   {@link getCameraSight} returned empty before it did any geometry — a filter on "can see it"
 *   admitted **nothing**, which is what the old picker's "No camera overlooks this yet" came from.
 *   (Since `camera-observer-setup-coverage` an unconfigured camera is calculated at the default
 *   preset, Wide 100° × 10 m, so it can reach bucket 1; the argument below is unchanged.)
 *
 * A shortlist containing everything and a shortlist containing nothing are equally useless, but
 * only the second one hides things. So `rankFeedsForEntity` ranks and never removes: a camera the
 * geometry dislikes sinks, and the operator can still reach it — the plan's cameras are often
 * missing or wrong, and an entity's camera choice must never hide a feed.
 *
 * ## Camera → entities: filtered, with a way round it
 *
 * The other direction FILTERS (`feedSight`; Rob, 2026-09-28, superseding the archived
 * `camera-observer-setup` rows 29 and 31 for this direction only). The camera workspace lists only
 * what the camera sees on its floor, and keeps every entity reachable two ways: a *Show
 * everything* switch, and a feed with no camera on the plan listing everything rather than
 * nothing.
 *
 * ## The three buckets (entity → cameras)
 *
 * 1. The camera's cone contains the entity — nearest camera first.
 * 2. A camera is placed and linked to the feed, but its cone misses — nearest first.
 * 3. No camera is placed for that feed at all, so there is no geometry to reason with.
 *
 * Distance is the tiebreak because it is the part that survives bad inputs: with a 177 m cone —
 * or seven 40 m ones over a gym — everything lands in bucket 1 and distance does all the work,
 * and a camera whose cone genuinely misses lands in bucket 2 where distance still ranks it.
 *
 * ## Floors
 *
 * A placed camera belongs to one floor's design and cannot see another floor. So buckets 1 and 2
 * are only ever computed over the camera's own floor, and `feedSight` answers for that floor alone.
 */

/** Where a placed camera lives, resolved once per feed. */
export interface PlacedCamera {
  camera: FloorPlannerCamera;
  design: FloorPlannerDesign;
  floorIndex: number;
}

const centreOfSurface = (poly: FloorPlannerPoint[]): FloorPlannerPoint | null => {
  if (poly.length === 0) return null;
  return {
    x: poly.reduce((acc, p) => acc + p.x, 0) / poly.length,
    y: poly.reduce((acc, p) => acc + p.y, 0) / poly.length,
  };
};

const distance = (a: FloorPlannerPoint, b: FloorPlannerPoint): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

/** Every floor's current design, paired with its index. */
const currentDesigns = (
  layout: FloorPlannerLayout | null | undefined,
): Array<{ design: FloorPlannerDesign; floorIndex: number }> =>
  asArray(layout?.floors).map((floor, floorIndex) => ({
    design: findLatestDesign(asArray(floor?.designs)),
    floorIndex,
  }));

/**
 * The camera placed on the plan for each feed, by endpoint id.
 *
 * A feed with no entry here is bucket 3 — not an error, and on every production twin today the
 * normal case.
 */
const placedCamerasByFeed = (
  layout: FloorPlannerLayout | null | undefined,
): Map<number, PlacedCamera> => {
  const map = new Map<number, PlacedCamera>();
  for (const { design, floorIndex } of currentDesigns(layout)) {
    for (const camera of asArray(design?.cameras).filter(isLive)) {
      if (camera.rtspEndpointId == null) continue;
      // First placement wins. Two cameras on one feed is a plan defect rather than a case to
      // model, and picking one deterministically beats ranking the same feed twice.
      if (!map.has(camera.rtspEndpointId))
        map.set(camera.rtspEndpointId, { camera, design, floorIndex });
    }
  }
  return map;
};

/** The point an entity is measured from: an item's own position, a surface's centroid. */
const entityPoint = (design: FloorPlannerDesign, entityRefid: string): FloorPlannerPoint | null => {
  const item = asArray(design?.items)
    .filter(isLive)
    .find(i => i.refid === entityRefid);
  if (item) return { x: item.x, y: item.y };
  const surface = asArray(design?.surfaces)
    .filter(isLive)
    .find(s => s.refid === entityRefid);
  if (surface) return centreOfSurface(asArray(surface.poly));
  return null;
};

/**
 * One feed's place in the ranking: its bucket, and how far its placed camera stands from the
 * entity in plan metres (`Infinity` in bucket 3, where nothing is placed to measure from).
 */
export interface FeedScore {
  id: number;
  bucket: 1 | 2 | 3;
  distance: number;
}

/**
 * The twin's feeds, scored and ordered by how likely each is to see this entity — the rule
 * {@link rankFeedsForEntity} reads, exposed so the Observers stage can caption each suggestion
 * with why it sits where it does (`observers-page-stage`, FR22). One rule, two readers.
 *
 * Every feed it was given, in order — or `[]` when there is no likelihood information at all.
 */
export const scoreFeedsForEntity = (
  layout: FloorPlannerLayout | null | undefined,
  entityRefid: string,
  feeds: Array<{ id: string | number }>,
): FeedScore[] => {
  const placed = placedCamerasByFeed(layout);
  if (placed.size === 0) return [];

  const scored: FeedScore[] = [];

  for (const feed of feeds) {
    const id = Number(feed.id);
    if (!Number.isFinite(id)) continue;

    const entry = placed.get(id);
    if (!entry) {
      scored.push({ id, bucket: 3, distance: Number.POSITIVE_INFINITY });
      continue;
    }

    const point = entityPoint(entry.design, entityRefid);
    if (!point) {
      // The entity is not on this camera's floor — a camera cannot see another floor.
      scored.push({ id, bucket: 3, distance: Number.POSITIVE_INFINITY });
      continue;
    }

    const sight = getCameraSight(entry.design, entry.camera);
    const inCone =
      sight.surfaces.some(s => s.refid === entityRefid) ||
      sight.items.some(i => i.refid === entityRefid);

    scored.push({
      id,
      bucket: inCone ? 1 : 2,
      distance: distance({ x: entry.camera.x, y: entry.camera.y }, point),
    });
  }

  /*
   * ⚠ The shortlist is skipped when there is no likelihood information — NOT merely when the plan
   * holds no camera at all.
   *
   * `placed.size === 0` above is a fast path for the common case and is not the whole test. A plan
   * can retain a marker for an endpoint that no longer exists (a stream deleted after the plan was
   * drawn, or a marker never linked to a feed), and then `placed` is non-empty while not one of the
   * feeds we were handed has any geometry behind it. Every feed lands in bucket 3, the caller reads
   * a non-empty array as "there is a shortlist", and the operator is shown four arbitrary feeds
   * under the heading *Cameras likely to see this* — a confident claim with nothing behind it, on
   * the exact path FR2 exists to protect.
   *
   * Bucket 3 means "nothing to judge by", so all-bucket-3 and no-cameras-placed are the same
   * answer, and the docstring's promise — an empty result means no feed has a camera placed for it
   * — is only true if both produce one.
   */
  if (scored.every(entry => entry.bucket === 3)) return [];

  return scored.sort((a, b) => a.bucket - b.bucket || a.distance - b.distance);
};

/**
 * The camera placed on the plan for one feed — live, first placement wins — or `null` when no live
 * camera on the plan links to it (the Observers corner plan's coverage,
 * `observer-corner-plan-camera-framing`). The same lookup the ranking and {@link feedSight} read.
 */
export const placedCameraForFeed = (
  layout: FloorPlannerLayout | null | undefined,
  feedId: number,
): PlacedCamera | null => placedCamerasByFeed(layout).get(feedId) ?? null;

/**
 * The twin's feeds, ordered by how likely each is to see this entity.
 *
 * Returns endpoint ids, and **returns every feed it was given** — the ordering is advice, not a
 * filter. An empty result means only one thing: no feed has a camera placed for it, so there is no
 * likelihood information at all and the caller should skip the shortlist step rather than render
 * an empty one.
 */
export const rankFeedsForEntity = (
  layout: FloorPlannerLayout | null | undefined,
  entityRefid: string,
  feeds: Array<{ id: string | number }>,
): number[] => scoreFeedsForEntity(layout, entityRefid, feeds).map(entry => entry.id);

/** What a feed's placed camera sees on the plan: the camera → entities half of the one rule. */
export interface FeedSight {
  /** The floor the camera is placed on — the only floor it can see. */
  floorIndex: number;
  /** Refids of the zones and machines inside the camera's cone, walls occluding. */
  inView: ReadonlySet<string>;
}

/**
 * What this feed's camera can see on the plan, or `null` when no live camera on the plan links to
 * the feed.
 *
 * The same `placedCamerasByFeed` and {@link getCameraSight} that `rankFeedsForEntity` reads, so
 * both directions answer from one rule. `null` is **absent**, not an error: the plan simply has no
 * cone for this feed, and the caller lists everything rather than nothing.
 */
export const feedSight = (
  layout: FloorPlannerLayout | null | undefined,
  feedId: number,
): FeedSight | null => {
  const placed = placedCameraForFeed(layout, feedId);
  if (!placed) return null;
  const sight = getCameraSight(placed.design, placed.camera);
  return {
    floorIndex: placed.floorIndex,
    inView: new Set([...sight.surfaces.map(s => s.refid), ...sight.items.map(i => i.refid)]),
  };
};
